import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import type { Team } from '../../types';
import MapShareMenu, { type MapShareMenuItem } from '../map/MapShareMenu';
import SnapshotModal from '../SnapshotModal';
import MemeModal from '../MemeModal';
import RivalryShareModal from '../RivalryShareModal';
import { composeSnapshotFromMapCanvas, isCanvasTaintError } from '../../utils/snapshot';
import { empireLeaderMeta, pickKeyDates } from '../../utils/empireStats';
import { formatDisplayDate } from '../../utils/date';
import { formatPopulation } from '../../utils/formatPopulation';
import { buildShareCaption, captionWithHashtags } from '../../utils/shareCaptions';
import { renderShareCard } from '../../utils/shareCard';
import { encodeGifFromPngBlobs } from '../../utils/timelapse';
import { timelapseLimitsForTier } from '../../utils/emperorTier';
import { buildMemeContext } from '../../utils/buildMemeContext';
import type { MemeContext } from '../../utils/memeTypes';
import { ownershipService } from '../../services/ownershipService';
import type { OwnMapEngine } from './ownMapEngine';
import type { OwnMapOwnership, OwnMapSelection, OwnMapTeam } from './ownMapTypes';
import { buildEmpireStats, rankEmpiresByCounties } from './empireStats';
import '../map/MapChrome.css';

const LEAGUE_NAME = 'NCAA Basketball';
const SITE_FALLBACK = 'https://empiremaps.live';

export type OwnMapOwnershipFactory = (
  rows: Awaited<ReturnType<typeof ownershipService.getOwnershipByLeague>>['ownership'],
  snapshotDate: string | null,
  rosterById: Map<number, Team>
) => OwnMapOwnership;

interface Props {
  leagueId: string;
  engine: OwnMapEngine | null;
  ownership: OwnMapOwnership | null;
  pick: OwnMapSelection | null;
  selectedDate: string;
  selectedSeason: number | null;
  dates: string[];
  popByFips: Record<string, number | null>;
  countyLabels: Record<string, string>;
  chromeHidden: boolean;
  onChromeHidden: (hidden: boolean) => void;
  toOwnership: OwnMapOwnershipFactory;
  rosterById: Map<number, Team>;
  onFrameOwnership: (own: OwnMapOwnership) => void;
  onRestoreOwnership: () => void;
}

function teamLeaderLine(
  ownership: OwnMapOwnership | null,
  popByFips: Record<string, number | null>,
  countyLabels: Record<string, string>,
  focused: OwnMapTeam | null
) {
  if (!ownership) return null;
  if (focused) {
    const stats = buildEmpireStats(ownership, focused.id, popByFips, countyLabels);
    if (!stats) return null;
    return {
      name: stats.team.name,
      color: stats.team.color,
      meta: empireLeaderMeta(stats.counties, stats.countyShare, stats.population, stats.popShare),
      isChampion: false,
      counties: stats.counties,
      share: stats.countyShare,
      population: stats.population,
    };
  }
  const ranked = rankEmpiresByCounties(ownership);
  const top = ranked[0];
  if (!top) return null;
  const stats = buildEmpireStats(ownership, top.teamId, popByFips, countyLabels);
  if (!stats) {
    return {
      name: top.team.name,
      color: top.team.color,
      meta: empireLeaderMeta(top.counties, top.share, 0, 0),
      isChampion: ranked.length === 1,
      counties: top.counties,
      share: top.share,
      population: 0,
    };
  }
  return {
    name: stats.team.name,
    color: stats.team.color,
    meta: empireLeaderMeta(stats.counties, stats.countyShare, stats.population, stats.popShare),
    isChampion: ranked.length === 1,
    counties: stats.counties,
    share: stats.countyShare,
    population: stats.population,
  };
}

const OwnMapShareControls: React.FC<Props> = ({
  leagueId,
  engine,
  ownership,
  pick,
  selectedDate,
  selectedSeason,
  dates,
  popByFips,
  countyLabels,
  chromeHidden,
  onChromeHidden,
  toOwnership,
  rosterById,
  onFrameOwnership,
  onRestoreOwnership,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [snapshotting, setSnapshotting] = useState(false);
  const [memeLoading, setMemeLoading] = useState(false);
  const [timelapsing, setTimelapsing] = useState(false);
  const [weeklySwingLoading, setWeeklySwingLoading] = useState(false);
  const [copyLinkLabel, setCopyLinkLabel] = useState<string | null>(null);
  const [snapshotBlob, setSnapshotBlob] = useState<Blob | null>(null);
  const [timelapseBlob, setTimelapseBlob] = useState<Blob | null>(null);
  const [weeklySwingBlob, setWeeklySwingBlob] = useState<Blob | null>(null);
  const [weeklySwingCaption, setWeeklySwingCaption] = useState<ReturnType<
    typeof buildShareCaption
  > | null>(null);
  const [memeContext, setMemeContext] = useState<MemeContext | null>(null);
  const [rivalryShareOpen, setRivalryShareOpen] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const focusedTeam =
    pick?.mode === 'territory' || pick?.mode === 'county' ? pick.team : null;
  const leader = useMemo(
    () => teamLeaderLine(ownership, popByFips, countyLabels, focusedTeam),
    [ownership, popByFips, countyLabels, focusedTeam]
  );

  const snapshotCard = (dateLabel?: string) => ({
    title: LEAGUE_NAME,
    subtitle: dateLabel
      ? `As of ${formatDisplayDate(dateLabel)}`
      : selectedDate
        ? `As of ${formatDisplayDate(selectedDate)}`
        : undefined,
    leaderLabel: leader?.isChampion
      ? t('standings.nationalChampion')
      : focusedTeam
        ? t('standings.focusedEmpire', { defaultValue: 'Focused empire' })
        : t('standings.currentLeader'),
    leaderName: leader?.name,
    leaderColor: leader?.color,
    leaderMeta: leader?.meta,
    siteUrl: 'empiremaps.live',
  });

  const captureBranded = async (dateForSubtitle?: string) => {
    if (!engine) throw new Error('Map not ready');
    const card = snapshotCard(dateForSubtitle);
    try {
      return await composeSnapshotFromMapCanvas(engine.captureCanvas(), card);
    } catch (err) {
      // CDN logos without CORS taint the map canvas — retry export without logos.
      if (!isCanvasTaintError(err)) throw err;
      return composeSnapshotFromMapCanvas(engine.captureCanvas({ logos: false }), card);
    }
  };

  const withHiddenChrome = async <T,>(fn: () => Promise<T>): Promise<T> => {
    const wasHidden = chromeHidden;
    onChromeHidden(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      return await fn();
    } finally {
      if (!wasHidden) onChromeHidden(false);
    }
  };

  const handleSnapshot = async () => {
    if (!engine || snapshotting || timelapsing || memeLoading) return;
    setSnapshotting(true);
    setShareError(null);
    try {
      const blob = await withHiddenChrome(() => captureBranded());
      setSnapshotBlob(blob);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Snapshot failed');
    } finally {
      setSnapshotting(false);
    }
  };

  const handleMeme = async () => {
    if (!engine || memeLoading || snapshotting || timelapsing) return;
    setMemeLoading(true);
    setShareError(null);
    try {
      const blob = await withHiddenChrome(() => captureBranded());
      const mapUrl =
        typeof window !== 'undefined' ? window.location.href : SITE_FALLBACK;
      const ctx = await buildMemeContext({
        leagueId,
        leagueName: LEAGUE_NAME,
        ownership,
        pick,
        selectedDate,
        selectedSeason,
        popByFips,
        countyLabels,
        mapBlob: blob,
        mapUrl,
      });
      setMemeContext(ctx);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Could not open meme generator');
    } finally {
      setMemeLoading(false);
    }
  };

  const handleTimelapse = async () => {
    if (!engine || timelapsing || snapshotting || dates.length < 2) return;
    setTimelapsing(true);
    setShareError(null);
    const limits = timelapseLimitsForTier(user?.account_tier);
    const framesToRender = pickKeyDates(dates, limits.maxFrames);
    try {
      const pngFrames = await withHiddenChrome(async () => {
        const frames: Blob[] = [];
        for (const date of framesToRender) {
          const res = await ownershipService.getOwnershipByLeague(
            leagueId,
            date,
            selectedSeason ?? undefined
          );
          const converted = toOwnership(res.ownership, res.date || date, rosterById);
          onFrameOwnership(converted);
          await new Promise((r) => setTimeout(r, 50));
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          frames.push(await captureBranded(date));
        }
        return frames;
      });
      onRestoreOwnership();
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const gif = await encodeGifFromPngBlobs(pngFrames, {
        delayMs: 450,
        maxWidth: limits.maxWidth,
        watermark: limits.watermark,
      });
      setTimelapseBlob(gif);
    } catch (e) {
      onRestoreOwnership();
      setShareError(e instanceof Error ? e.message : 'Timelapse export failed');
    } finally {
      setTimelapsing(false);
    }
  };

  const handleWeeklySwing = async () => {
    if (weeklySwingLoading) return;
    setWeeklySwingLoading(true);
    setShareError(null);
    try {
      const qs = new URLSearchParams();
      if (selectedSeason != null) qs.set('season', String(selectedSeason));
      if (selectedDate) qs.set('date', selectedDate.slice(0, 10));
      const q = qs.toString();
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || '/api/v2'}/viral/weekly-swing/${encodeURIComponent(leagueId)}${q ? `?${q}` : ''}`
      );
      const data = await res.json();
      if (!data.success || !data.swing) {
        setShareError(
          selectedDate
            ? `No big land swing in the week ending ${selectedDate.slice(0, 10)}.`
            : 'No big land swing in the last 7 days yet.'
        );
        return;
      }
      const swing = data.swing;
      const caption = buildShareCaption({
        hook: 'swing',
        leagueName: LEAGUE_NAME,
        teamName: swing.winner_name,
        rivalName: swing.loser_name,
        countyName: `${swing.counties_flipped} counties`,
      });
      const blob = await renderShareCard({
        headline: `${swing.winner_name} flipped ${swing.counties_flipped} counties`,
        subhead: swing.loser_name
          ? `Beat ${swing.loser_name}${swing.winner_score != null ? ` (${swing.winner_score}–${swing.loser_score})` : ''}`
          : 'Biggest swing this week',
        accentColor: swing.accent_color || '#f97316',
        badge: 'WEEKLY SWING',
      });
      setWeeklySwingCaption(caption);
      setWeeklySwingBlob(blob);
    } catch (e) {
      setShareError(e instanceof Error ? e.message : 'Could not load weekly swing');
    } finally {
      setWeeklySwingLoading(false);
    }
  };

  const rivalryTeams = useMemo(() => {
    if (!ownership) return [];
    return rankEmpiresByCounties(ownership).map((r) => {
      const team = ownership.teams[String(r.teamId)] || r.team;
      const roster = rosterById.get(r.teamId);
      return {
        team_id: r.teamId,
        team_name: team.name,
        color: team.color,
        share: r.share,
        counties: r.counties,
        logo_url: team.logo_url || roster?.logo_url || null,
        espn_team_id: team.espn_team_id ?? roster?.espn_team_id ?? null,
      };
    });
  }, [ownership, rosterById]);

  const handleCopyLink = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : SITE_FALLBACK;
    try {
      await navigator.clipboard.writeText(url);
      setCopyLinkLabel(t('map.linkCopied', { defaultValue: 'Copied!' }));
      window.setTimeout(() => setCopyLinkLabel(null), 1600);
    } catch {
      setShareError('Could not copy link');
    }
  };

  const items: MapShareMenuItem[] = [
    {
      id: 'copy',
      label: copyLinkLabel
        ? `✓ ${copyLinkLabel}`
        : `🔗 ${t('map.copyLink', { defaultValue: 'Copy link' })}`,
      onClick: handleCopyLink,
    },
    {
      id: 'snapshot',
      label: `📸 ${t('map.snapshot')}`,
      onClick: handleSnapshot,
      disabled: snapshotting || timelapsing || memeLoading || !engine,
      busy: snapshotting,
    },
    {
      id: 'meme',
      label: `📢 ${t('map.meme')}`,
      onClick: handleMeme,
      disabled: memeLoading || snapshotting || timelapsing || !engine,
      busy: memeLoading,
    },
    ...(dates.length >= 2
      ? [
          {
            id: 'gif',
            label: `🎬 ${t('map.gif')}`,
            onClick: handleTimelapse,
            disabled: timelapsing || snapshotting || !engine,
            busy: timelapsing,
          } satisfies MapShareMenuItem,
        ]
      : []),
    {
      id: 'swing',
      label: `📈 ${t('map.weeklySwing')}`,
      onClick: handleWeeklySwing,
      disabled: weeklySwingLoading,
      busy: weeklySwingLoading,
    },
    {
      id: 'rivalry',
      label: `🔥 ${t('map.rivalry')}`,
      onClick: () => setRivalryShareOpen(true),
      disabled: rivalryTeams.length < 2,
    },
  ];

  const shareUrl = typeof window !== 'undefined' ? window.location.href : SITE_FALLBACK;

  return (
    <>
      <div className="em-share-wrap">
        <MapShareMenu items={items} />
        <button
          type="button"
          className={`map-chrome-btn em-hide-ui-btn${chromeHidden ? ' is-active' : ''}`}
          aria-pressed={chromeHidden}
          aria-label={chromeHidden ? 'Show UI' : 'Hide UI'}
          title={chromeHidden ? 'Show UI' : 'Hide UI'}
          onClick={() => onChromeHidden(!chromeHidden)}
        >
          <svg className="em-hide-ui-btn__icon" viewBox="0 0 24 24" aria-hidden>
            {chromeHidden ? (
              <>
                <path
                  d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
              </>
            ) : (
              <>
                <path
                  d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="1.8" />
              </>
            )}
          </svg>
          <span className="em-hide-ui-btn__label">{chromeHidden ? 'Show UI' : 'Hide UI'}</span>
        </button>
        {shareError && (
          <span className="em-share-error" role="status">
            {shareError}
          </span>
        )}
      </div>

      {timelapseBlob && (
        <SnapshotModal
          blob={timelapseBlob}
          fileName={`empire-maps-${leagueId}-timelapse.gif`}
          shareTitle={`Empire Maps timelapse — ${LEAGUE_NAME}`}
          shareText={`Watch the ${LEAGUE_NAME} empire shift across the season on Empire Maps.`}
          shareUrl={shareUrl}
          onClose={() => setTimelapseBlob(null)}
        />
      )}
      {snapshotBlob && (
        <SnapshotModal
          blob={snapshotBlob}
          fileName={`empire-maps-${leagueId}.png`}
          shareTitle={`Empire Maps — ${LEAGUE_NAME}`}
          shareText={
            leader
              ? `${leader.isChampion ? t('standings.nationalChampion') : t('standings.currentLeader')}: ${t('standings.controlsLand', { team: leader.name, percent: Math.round(leader.share * 100) })}${leader.population > 0 ? t('standings.peopleSuffix', { population: formatPopulation(leader.population) }) : ''} on Empire Maps.`
              : `${LEAGUE_NAME} conquest map on Empire Maps.`
          }
          shareUrl={shareUrl}
          onClose={() => setSnapshotBlob(null)}
        />
      )}
      {memeContext && (
        <MemeModal context={memeContext} onClose={() => setMemeContext(null)} />
      )}
      {rivalryShareOpen && (
        <RivalryShareModal
          leagueId={leagueId}
          leagueName={LEAGUE_NAME}
          teams={rivalryTeams}
          onClose={() => setRivalryShareOpen(false)}
        />
      )}
      {weeklySwingBlob && weeklySwingCaption && (
        <SnapshotModal
          blob={weeklySwingBlob}
          fileName={`empire-maps-weekly-swing-${leagueId}.png`}
          shareTitle={weeklySwingCaption.title}
          shareText={captionWithHashtags(weeklySwingCaption)}
          shareUrl={shareUrl}
          onClose={() => {
            setWeeklySwingBlob(null);
            setWeeklySwingCaption(null);
          }}
        />
      )}
    </>
  );
};

export default OwnMapShareControls;
